// API Client for SmartOps - Fixed version
// Connects to actual backend services

export interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'down';
  description?: string;
  lastChecked?: string;
  port?: number;
  url?: string;
}

export interface SystemMetrics {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  network_io: number;
  active_services: number;
  healthy_services: number;
  error_rate: number;
  response_time: number;
  timestamp: string;
}

export interface AIInsight {
  id: string;
  type: string;
  confidence: number;
  message: string;
  recommendation: string;
  impact: string;
  urgency: string;
  data: Record<string, any>;
  timestamp: string;
}

import { ENV_CONFIG, getServiceConfig } from '../config/environment';

// API Client class
export class APIClient {
  private baseURL: string;

  constructor(baseURL: string = ENV_CONFIG.SMART_OPS_BACKEND_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Only log errors for non-health endpoints to reduce console noise
      if (!endpoint.includes('/health')) {
        console.warn(`API request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }

  // Generic HTTP methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Smart-Ops Backend APIs
  async getSystemHealth(): Promise<any> {
    return this.request('/health');
  }

  async getSystemOverview(): Promise<any> {
    return this.request('/');
  }

  // AI Service APIs - Use actual AI service
  async getAIServiceHealth(): Promise<any> {
    const aiClient = new APIClient(ENV_CONFIG.AI_SERVICE_URL);
    return aiClient.request('/health');
  }

  async getAIInsights(): Promise<AIInsight[]> {
    return this.request('/api/v1/ai/insights');
  }

  async analyzeSystem(metrics: Partial<SystemMetrics>): Promise<any> {
    return this.request('/api/v1/ai/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(metrics),
    });
  }

  // RAG Service APIs - Use actual RAG service
  async getRAGServiceHealth(): Promise<any> {
    const ragClient = new APIClient(ENV_CONFIG.RAG_SERVICE_URL);
    return ragClient.request('/health');
  }

  async getRAGServices(): Promise<any> {
    return this.request('/api/v1/rag/services');
  }

  // Dashboard Statistics - Use Smart-Ops backend endpoints
  async getDashboardStats(): Promise<any> {
    try {
      const [metrics, serviceStatuses] = await Promise.allSettled([
        this.request<any>('/api/v1/dashboard/metrics'),
        this.request<any>('/api/v1/dashboard/service-statuses'),
      ]);

      return {
        metrics: metrics.status === 'fulfilled' ? metrics.value.data : null,
        serviceStatuses: serviceStatuses.status === 'fulfilled' ? serviceStatuses.value.data : null,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      return {
        metrics: null,
        serviceStatuses: null,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Service Status - Use Smart-Ops backend endpoint
  async getServiceStatuses(): Promise<ServiceStatus[]> {
    try {
      const response = await this.request<any>('/api/v1/dashboard/service-statuses');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch service statuses from backend, using fallback:', error);
      // Fallback to individual health checks
      const [systemHealth, aiHealth, ragHealth] = await Promise.allSettled([
        this.getSystemHealth(),
        this.getAIServiceHealth(),
        this.getRAGServiceHealth(),
      ]);

      const services: ServiceStatus[] = [];

      // Smart-Ops Backend
      const smartOpsConfig = getServiceConfig('smart-ops');
      if (systemHealth.status === 'fulfilled') {
        services.push({
          name: smartOpsConfig.name,
          status: 'operational',
          description: 'Main backend API',
          lastChecked: new Date().toISOString(),
          port: smartOpsConfig.port,
          url: smartOpsConfig.url,
        });
      } else {
        services.push({
          name: smartOpsConfig.name,
          status: 'down',
          description: 'Backend API unavailable',
          lastChecked: new Date().toISOString(),
          port: smartOpsConfig.port,
          url: smartOpsConfig.url,
        });
      }

      // AI Service
      const aiConfig = getServiceConfig('ai-service');
      if (aiHealth.status === 'fulfilled') {
        services.push({
          name: aiConfig.name,
          status: 'operational',
          description: 'AI and LLM services',
          lastChecked: new Date().toISOString(),
          port: aiConfig.port,
          url: aiConfig.url,
        });
      } else {
        services.push({
          name: aiConfig.name,
          status: 'down',
          description: 'AI service unavailable',
          lastChecked: new Date().toISOString(),
          port: aiConfig.port,
          url: aiConfig.url,
        });
      }

      // RAG Service
      const ragConfig = getServiceConfig('rag-service');
      if (ragHealth.status === 'fulfilled') {
        services.push({
          name: ragConfig.name,
          status: 'operational',
          description: 'Document processing and retrieval',
          lastChecked: new Date().toISOString(),
          port: ragConfig.port,
          url: ragConfig.url,
        });
      } else {
        services.push({
          name: ragConfig.name,
          status: 'down',
          description: 'RAG service unavailable',
          lastChecked: new Date().toISOString(),
          port: ragConfig.port,
          url: ragConfig.url,
        });
      }

      return services;
    }
  }
}

// Export singleton instance
export const apiClient = new APIClient();

// Legacy function for backward compatibility
export const getServiceStatuses = async (): Promise<ServiceStatus[]> => {
  return apiClient.getServiceStatuses();
}; 